import type { AxiosRequestConfig } from 'axios';

import type {
  TQLHIAlertHint,
  TQLHIAlertHintCreate,
  TQLHIEventHint,
  TQLHIEventHintCreate,
  TQLHIEventHintUpdate,
  TQLHILinkInfo,
  TQLHILinkDepend,
  TQLHIStatic,
  TQLHIStaticCreate,
  TQLHIStaticUpdate,
} from '@/entities/monitoring/hi/@x/monitoring-template';
import type {
  HIGroupHistoryHiTypesRequest,
  HIGroupHistoryHiTypesResponse,
} from '@/entities/monitoring/hi/@x/monitoring-template';
import { Page, PaginationParams } from '@/shared/api/pagination';
import type {
  CreateMonitoringTemplateMetric,
  CreateMonitoringTemplateRequest,
  CopyMonitoringTemplateRequest,
  CopyMonitoringTemplateResponse,
  HITDepends,
  MonitoringTemplate,
  MonitoringTemplateExport,
  MonitoringTemplateHITResponse,
  MonitoringTemplateMetricV4,
  UpdateMonitoringTemplateMetric,
  UpdateMonitoringTemplateRequest,
} from '../model/monitoring-template';

import { monitoringInstance } from '@/shared/api/api-instances/monitoring/api-instance';
import { ImportEntities } from '@/shared/api/import-contracts';

type MetricSchemas = {
  template_name: string;
  metric_name: string;
};
export type RawHIPayload = {
  node_id: string | number;
  arguments: number[];
  metric_name: string;
  hi_type_name: string;
  template_name: string;
  rule?: string;
  vmalert_names?: string[] | null;
};
export type RawHIUpdatePayload = Omit<RawHIPayload, 'template_name'>;
export type CalculateHIPayload = {
  node_id: string | number;
  expression: number;
  hi_type_name: string;
  template_name: string;
  arguments?: Record<string, any>;
  depend_hi_link_ids?: TQLHILinkDepend[];
};
export type CalculateHIUpdatePayload = Omit<
  CalculateHIPayload,
  'template_name'
>;
const baseURL = '/api/monitoring/v4/';
const defaultOptions = { baseURL };

export class MonitoringTemplatesApi {
  static getTemplates(params: PaginationParams, options?: AxiosRequestConfig) {
    return monitoringInstance.get<Page<MonitoringTemplate>>('/templates', {
      ...options,
      params,
    });
  }

  static getTemplate(name: string) {
    return monitoringInstance.get<MonitoringTemplate>(`/template/${name}`);
  }

  /**
   * Шаблоны по именам. В отличие от getTemplates отдаёт полные шаблоны вместе с
   * запросом TQL — список же приходит без него, только id, name и label.
   */
  static getTemplatesByNames(names: string[], options?: AxiosRequestConfig) {
    return monitoringInstance.post<MonitoringTemplate[]>(
      '/templates',
      names,
      options,
    );
  }

  static createTemplate(
    payload: CreateMonitoringTemplateRequest,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post<MonitoringTemplate>(
      '/template',
      payload,
      options,
    );
  }

  static copyTemplate(
    template_name: string,
    payload: CopyMonitoringTemplateRequest,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post<CopyMonitoringTemplateResponse>(
      `/template/${template_name}/copy`,
      payload,
      { ...defaultOptions, ...options },
    );
  }

  static updateTemplate(
    template_name: string,
    force: boolean = false,
    payload: UpdateMonitoringTemplateRequest,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.put<MonitoringTemplate>(
      `/template/${template_name}`,
      payload,
      {
        params: { force: force },
        ...options,
      },
    );
  }

  static deleteTemplate(
    template_name: string,
    force: boolean = false,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.delete<MonitoringTemplate>(
      `/template/${template_name}`,
      {
        params: { force: force },
        ...options,
      },
    );
  }

  static getMetricsV4(template_name: string, options?: AxiosRequestConfig) {
    return monitoringInstance.get<MonitoringTemplateMetricV4[]>(
      `/tql_metrics/for_template/${template_name}`,
      { ...defaultOptions, ...options },
    );
  }

  static getMetricByNameV4(
    metrics_schemas: MetricSchemas,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.get<MonitoringTemplateMetricV4>(
      `/template/${metrics_schemas.template_name}/tql_metric/${metrics_schemas.metric_name}`,
      { ...defaultOptions, ...options },
    );
  }

  static createMetric(
    template_name: string,
    payload: CreateMonitoringTemplateMetric,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post<MonitoringTemplate>(
      `/template/${template_name}/tql_metric`,
      payload,
      {
        ...defaultOptions,
        ...options,
      },
    );
  }

  static updateMetric(
    metrics_schemas: MetricSchemas,
    payload: UpdateMonitoringTemplateMetric,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.put<MonitoringTemplate>(
      `/template/${metrics_schemas.template_name}/tql_metric/${metrics_schemas.metric_name}`,
      payload,
      {
        ...defaultOptions,
        ...options,
      },
    );
  }

  static deleteMetric(
    metrics_schemas: MetricSchemas,
    force?: boolean,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.delete<MonitoringTemplate>(
      `/template/${metrics_schemas.template_name}/tql_metric/${metrics_schemas.metric_name}`,
      {
        ...options,
        params: { force },
      },
    );
  }

  static enableMetrics(
    template_name: string,
    payload: string[],
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post<string>(
      `/tql_metric/${template_name}/enable_all`,
      payload,
      options,
    );
  }

  static getHiTypesByTemplate(
    template_name: string,
    options: AxiosRequestConfig = { baseURL: '/api/monitoring/v4/' },
  ) {
    return monitoringInstance.get<MonitoringTemplateHITResponse[]>(
      `/hi/tql_links/template/${template_name}`,
      options,
    );
  }

  static getCalculatedHITypes(
    payload: HIGroupHistoryHiTypesRequest,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post<HIGroupHistoryHiTypesResponse>(
      '/hi/tql_links/hi_types',
      payload,
      {
        ...defaultOptions,
        ...options,
      },
    );
  }

  static getRawHI(hi_link_id: string, options?: AxiosRequestConfig) {
    return monitoringInstance.get<TQLHILinkInfo>(
      `/hi/tql_hi_link/raw/${hi_link_id}`,
      { ...defaultOptions, ...options },
    );
  }

  static createRawHI(payload: RawHIPayload, options?: AxiosRequestConfig) {
    return monitoringInstance.post<TQLHILinkInfo>(
      '/hi/tql_hi_link/raw',
      payload,
      { ...defaultOptions, ...options },
    );
  }

  static updateRawHI(
    hi_link_id: string,
    payload: RawHIUpdatePayload,
    options?: AxiosRequestConfig,
  ) {
    const { params, ...restOptions } = options || {};

    return monitoringInstance.put<TQLHILinkInfo>(
      `/hi/tql_hi_link/raw/${hi_link_id}`,
      payload,
      {
        ...defaultOptions,
        ...restOptions,
        params: { ...params, force: true },
      },
    );
  }

  static deleteRawHI(hi_link_id: string, options?: AxiosRequestConfig) {
    return monitoringInstance.delete<void>(
      `/hi/tql_hi_link/raw/${hi_link_id}`,
      { ...defaultOptions, ...options },
    );
  }

  static getCalculateHI(hi_link_id: string, options?: AxiosRequestConfig) {
    return monitoringInstance.get<TQLHILinkInfo>(
      `/hi/tql_hi_link/calculate/${hi_link_id}`,
      { ...defaultOptions, ...options },
    );
  }

  static createCalculateHI(
    payload: CalculateHIPayload,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post<TQLHILinkInfo>(
      '/hi/tql_hi_link/calculate',
      payload,
      { ...defaultOptions, ...options },
    );
  }

  static updateCalculateHI(
    hi_link_id: string,
    payload: CalculateHIUpdatePayload,
    options?: AxiosRequestConfig,
  ) {
    const { params, ...restOptions } = options || {};

    return monitoringInstance.put<TQLHILinkInfo>(
      `/hi/tql_hi_link/calculate/${hi_link_id}`,
      payload,
      {
        ...defaultOptions,
        ...restOptions,
        params: { ...params, force: true },
      },
    );
  }

  static deleteCalculateHI(hi_link_id: string, options?: AxiosRequestConfig) {
    return monitoringInstance.delete<void>(
      `/hi/tql_hi_link/calculate/${hi_link_id}`,
      { ...defaultOptions, ...options },
    );
  }

  static getDepends(
    template_name: string,
    src_node_id: string,
    dst_node_id: string,
    hi_link_id?: string,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.get<HITDepends[]>(
      `/hi/tql_links/depends/template/${template_name}/src_node/${src_node_id}/dst_node/${dst_node_id}`,
      { ...defaultOptions, ...options, params: { hi_link_id: hi_link_id } },
    );
  }

  static enableHIs(
    template_name: string,
    payload: string[],
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post<string>(
      `/hi/tql_link/${template_name}/enable_all_v2`,
      payload,
      {
        ...defaultOptions,
        ...options,
      },
    );
  }

  static getDefaultTemplate() {
    return monitoringInstance.get<string>('/tql_metrics/template');
  }

  // static disableMetrics(metrics_schemas: MetricSchemas[]) {
  //   return monitoringInstance.delete<string>('/tql_metrics/disable', {
  //     data: metrics_schemas,
  //   });
  // }

  static analyzeTemplatesEntities(
    json: MonitoringTemplateExport,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post('/import/templates/analyze', json, {
      ...defaultOptions,
      ...options,
    });
  }

  static executeTemplatesEntities(
    data: ImportEntities,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post('/import/templates/execute', data, {
      ...defaultOptions,
      ...options,
    });
  }

  static exportTemplatesV4(
    template_names: string[],
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post(`/templates/export/json`, template_names, {
      ...defaultOptions,
      ...options,
    });
  }

  static getAlertHintHI(hi_link_id: string, options?: AxiosRequestConfig) {
    return monitoringInstance.get<TQLHIAlertHint>(
      `/hi/tql_hi_link/alert_hint/${hi_link_id}`,
      {
        ...defaultOptions,
        ...options,
      },
    );
  }

  static createAlertHintHI(
    payload: TQLHIAlertHintCreate,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post<TQLHIAlertHint>(
      `/hi/tql_hi_link/alert_hint`,
      payload,
      {
        ...defaultOptions,
        ...options,
      },
    );
  }

  static updateAlertHintHI(
    hi_link_id: string,
    payload: Omit<TQLHIAlertHintCreate, 'template_name' | 'type'>,
    options?: AxiosRequestConfig,
  ) {
    const { params, ...restOptions } = options || {};

    return monitoringInstance.put<TQLHILinkInfo>(
      `/hi/tql_hi_link/alert_hint/${hi_link_id}`,
      payload,
      {
        ...defaultOptions,
        ...restOptions,
        params: {
          ...params,
          force: true,
        },
      },
    );
  }

  static deleteAlertHintHI(hi_link_id: string, options?: AxiosRequestConfig) {
    return monitoringInstance.delete<void>(
      `/hi/tql_hi_link/alert_hint/${hi_link_id}`,
      { ...defaultOptions, ...options },
    );
  }

  static getStaticHI(hi_link_id: string, options?: AxiosRequestConfig) {
    return monitoringInstance.get<TQLHIStatic>(
      `/hi/tql_hi_link/static/${hi_link_id}`,
      {
        ...defaultOptions,
        ...options,
      },
    );
  }

  static createStaticHI(
    payload: TQLHIStaticCreate,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post<TQLHIStatic>(
      `/hi/tql_hi_link/static`,
      payload,
      {
        ...defaultOptions,
        ...options,
      },
    );
  }

  static updateStaticHI(
    hi_link_id: string,
    payload: TQLHIStaticUpdate,
    options?: AxiosRequestConfig,
  ) {
    const { params, ...restOptions } = options || {};

    return monitoringInstance.put<TQLHIStatic>(
      `/hi/tql_hi_link/static/${hi_link_id}`,
      payload,
      {
        ...defaultOptions,
        ...restOptions,
        params: {
          ...params,
          force: true,
        },
      },
    );
  }

  static deleteStaticHI(hi_link_id: string, options?: AxiosRequestConfig) {
    return monitoringInstance.delete<void>(
      `/hi/tql_hi_link/static/${hi_link_id}`,
      { ...defaultOptions, ...options },
    );
  }

  static getEventHintHI(hi_link_id: string, options?: AxiosRequestConfig) {
    return monitoringInstance.get<TQLHIEventHint>(
      `/hi/tql_hi_link/event_hint/${hi_link_id}`,
      {
        ...defaultOptions,
        ...options,
      },
    );
  }

  static createEventHintHI(
    payload: TQLHIEventHintCreate,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post<TQLHIEventHint>(
      `/hi/tql_hi_link/event_hint`,
      payload,
      {
        ...defaultOptions,
        ...options,
      },
    );
  }

  static updateEventHintHI(
    hi_link_id: string,
    payload: TQLHIEventHintUpdate,
    options?: AxiosRequestConfig,
  ) {
    const { params, ...restOptions } = options || {};

    return monitoringInstance.put<TQLHIEventHint>(
      `/hi/tql_hi_link/event_hint/${hi_link_id}`,
      payload,
      {
        ...defaultOptions,
        ...restOptions,
        params: {
          ...params,
          force: true,
        },
      },
    );
  }

  static deleteEventHintHI(hi_link_id: string, options?: AxiosRequestConfig) {
    return monitoringInstance.delete<void>(
      `/hi/tql_hi_link/event_hint/${hi_link_id}`,
      { ...defaultOptions, ...options },
    );
  }

  static validateImport(
    json: { templates: MonitoringTemplate[] },
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post(`/import/templates/validate`, json, {
      ...defaultOptions,
      ...options,
    });
  }
}
